import { useState, useMemo, useEffect } from "react"
import { Plus, Search } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Input } from "@f2e/ui/components/base"
import { Brand, BrandEditData, Permission } from "@/types"
import { useToast } from "@/hooks"
import { useStore } from "@/store"
import { PermissionButton } from "@/components/Auth"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import {
  useGetBrandsApi,
  useCreateBrandApi,
  brandsResponseToBrands,
  brandResponseToBrand,
  FetchStatus,
} from "@/hooks/api"

import { BrandCard, BrandDialog } from "./components"

const BrandList = () => {
  const { t } = useTranslation()
  const { alertToast, successToast } = useToast()
  const [currentOrganization] = useStore((state) => [state.currentOrganization])

  const [brands, setBrands] = useState<Brand[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  const [isFormOpen, setIsFormOpen] = useState(false)

  // APIs
  const {
    data: brandsRespData,
    error: brandsRespError,
    status: brandsRespStatus,
    fetchFn: getBrands,
  } = useGetBrandsApi(currentOrganization?.id || "")

  const {
    data: createBrandRespData,
    error: createBrandRespError,
    status: createBrandRespStatus,
    fetchFn: createBrand,
  } = useCreateBrandApi(currentOrganization?.id || "")

  // Load brands on mount and when organization changes
  useEffect(() => {
    if (currentOrganization?.id) {
      getBrands({
        searchParams: new URLSearchParams({
          limit: "100",
          offset: "0",
        }),
      })
    }
  }, [currentOrganization?.id, getBrands])

  // Handle brands data
  useEffect(() => {
    if (!brandsRespData || !brandsRespData.data) return
    const transformedBrands = brandsResponseToBrands(brandsRespData.data)
    setBrands(transformedBrands)
  }, [brandsRespData])

  // Handle brands error
  useEffect(() => {
    if (!brandsRespError) return
    alertToast({
      title: t("brands.messages.error.title"),
      description: getUserFacingApiErrorMessage(
        brandsRespError,
        t,
        brandsRespData?.message || t("brands.messages.error.loadBrands")
      ),
    })
  }, [brandsRespError, brandsRespData, alertToast, t])

  // Handle create brand success
  useEffect(() => {
    if (!createBrandRespData || !createBrandRespData.data) return

    const newBrand = brandResponseToBrand(createBrandRespData.data)
    setBrands((prevBrands) => [...prevBrands, newBrand])
    successToast({
      title: t("brands.messages.created.title"),
      description: t("brands.messages.created.description"),
    })
  }, [createBrandRespData, successToast, t])

  // Handle create brand error
  useEffect(() => {
    if (!createBrandRespError) return
    alertToast({
      title: t("brands.messages.error.title"),
      description: getUserFacingApiErrorMessage(
        createBrandRespError,
        t,
        createBrandRespData?.message || t("brands.messages.error.createBrand")
      ),
    })
  }, [createBrandRespError, createBrandRespData, alertToast, t])

  // Filter brands based on search query
  const filteredBrands = useMemo(() => {
    if (!searchQuery.trim()) return brands

    const query = searchQuery.toLowerCase()
    return brands.filter(
      (brand) =>
        brand.name.toLowerCase().includes(query) ||
        brand.description.toLowerCase().includes(query)
    )
  }, [brands, searchQuery])

  const handleCreateBrand = (data: BrandEditData) => {
    createBrand({
      jsonBody: {
        name: data.name,
        description: data.description,
      },
    })
    setIsFormOpen(false)
  }

  const isLoading = brandsRespStatus === FetchStatus.Loading
  const isCreating = createBrandRespStatus === FetchStatus.Loading

  return (
    <div className="space-y-6 mt-4 px-2 pb-10">
      {/* Header */}
      <div className="space-y-0.5">
        <h2 className="text-2xl font-bold tracking-tight">
          {t("brands.title")}
        </h2>
        <p className="text-muted-foreground">{t("brands.description")}</p>
      </div>

      {/* Search and Create */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("brands.list.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <PermissionButton
          permission={Permission.OrgUpdateConnection}
          disabledTooltip={t("common.noPermission")}
          onClick={() => setIsFormOpen(true)}
        >
          <Plus className="mr-1 h-4 w-4" />
          {t("brands.list.createBrand")}
        </PermissionButton>
      </div>

      {/* Brand Grid */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-muted-foreground">{t("common.loading")}</p>
        </div>
      ) : filteredBrands.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-muted-foreground">{t("brands.list.noBrands")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBrands.map((brand) => (
            <BrandCard
              key={brand.id}
              brand={brand}
              workspaceCount={brand.workspaceCount}
            />
          ))}
        </div>
      )}

      {/* Brand Dialog */}
      <BrandDialog
        open={isFormOpen}
        isLoading={isCreating}
        onOpenChange={setIsFormOpen}
        onSubmit={handleCreateBrand}
      />
    </div>
  )
}

export default BrandList
